/**
 * Wheel (simple) — shared contracts for storefront, admin, and backend.
 * Spinable segments use one rule everywhere: enabled === true, weight > 0, finite, original order preserved.
 */
/** Canonical wheel engine for weighted reward segments (server spin). */
export const WHEEL_ENGINE_VERSION_DEFAULT = "weighted-rewards-v1";
/**
 * Storefront/admin presentation preset for the wheel family (not validated for gameplay).
 * Extend with new values as visual variants are added.
 */
export const WHEEL_VISUAL_VARIANT_DEFAULT = "simple-svg-v1";
/** v1 interaction kinds (extend later: progressive, jackpot). */
export const WHEEL_INTERACTION_TYPES_V1 = [
    "instant",
    "lead_capture",
    /** Commerce-first: spin immediately, outcomes are discount-oriented (see reward types). */
    "instant_discount",
];
export const WHEEL_LEAD_FIELDS = ["email", "phone"];
export const WHEEL_REWARD_TYPES = [
    "message",
    "discount_percent",
    /** Checkout code string (merchant-defined); distinct from percentage off. */
    "discount_code",
    "points",
    "free_shipping",
];
export function isWheelRewardType(v) {
    return typeof v === "string" && WHEEL_REWARD_TYPES.includes(v);
}
/**
 * Apply defaults for persisted configs that predate engine metadata fields.
 */
/** UI helpers only — canonical defaults for persisted JSON are applied in Zod (backend). */
export function withWheelInteractionDefaults(config) {
    if (!config.interaction) {
        return { ...config, interaction: { type: "instant" } };
    }
    if (config.interaction.type === "lead_capture") {
        const fields = config.interaction.requiredFields && config.interaction.requiredFields.length > 0
            ? config.interaction.requiredFields
            : ["email"];
        return {
            ...config,
            interaction: { type: "lead_capture", requiredFields: fields },
        };
    }
    if (config.interaction.type === "instant_discount") {
        return { ...config, interaction: { type: "instant_discount" } };
    }
    return { ...config, interaction: { type: "instant" } };
}
export function withWheelEngineDefaults(config) {
    const withMeta = {
        ...config,
        engineVersion: config.engineVersion ?? WHEEL_ENGINE_VERSION_DEFAULT,
        visualVariant: config.visualVariant ?? WHEEL_VISUAL_VARIANT_DEFAULT,
    };
    return withWheelInteractionDefaults(withMeta);
}
/** Catalog / campaign template keys (wheel engine presets). */
export const WHEEL_CATALOG_TEMPLATE_IDS = ["lead_capture", "instant_discount"];
export function isWheelCatalogTemplateId(v) {
    return (typeof v === "string" &&
        WHEEL_CATALOG_TEMPLATE_IDS.includes(v));
}
function newSegmentId() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function") {
        return c.randomUUID();
    }
    return `seg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}
function newSeg(partial) {
    return {
        id: partial.id ?? newSegmentId(),
        label: partial.label,
        weight: partial.weight,
        rewardType: partial.rewardType,
        rewardValue: partial.rewardValue,
        enabled: partial.enabled,
    };
}
/**
 * Code defaults for wheel catalog templates (used when merchant has no saved defaults).
 */
export function buildDefaultWheelConfigForTemplate(templateId) {
    if (templateId === "lead_capture") {
        return withWheelEngineDefaults({
            segments: [
                newSeg({
                    label: "10% off",
                    weight: 1,
                    rewardType: "discount_percent",
                    rewardValue: 10,
                    enabled: true,
                }),
                newSeg({
                    label: "Try again",
                    weight: 1,
                    rewardType: "message",
                    rewardValue: "Better luck next time",
                    enabled: true,
                }),
            ],
            interaction: { type: "lead_capture", requiredFields: ["email"] },
        });
    }
    return withWheelEngineDefaults({
        segments: [
            newSeg({
                label: "10% off code",
                weight: 1,
                rewardType: "discount_code",
                rewardValue: "SAVE10",
                enabled: true,
            }),
            newSeg({
                label: "Try again",
                weight: 1,
                rewardType: "message",
                rewardValue: "Better luck next time",
                enabled: true,
            }),
        ],
        interaction: { type: "instant_discount" },
    });
}
export const DEFAULT_WHEEL_PREVIEW_COLORS = {
    pointerHex: "#202223",
    rimStrokeHex: "#e1e3e5",
    outerRingHex: "#f6f6f7",
    segmentHexes: ["#5c6ac3", "#47c1bf", "#9c6ade", "#f49342", "#50b83c", "#de3618"],
};
/**
 * Segments that participate in weighted spin and SVG layout (same index as resultIndex).
 * Order matches the first occurrence in `segments` (non-spinable rows are skipped).
 */
export function getSpinableSegments(segments) {
    return segments.filter((s) => s.enabled === true &&
        typeof s.weight === "number" &&
        Number.isFinite(s.weight) &&
        s.weight > 0);
}
//# sourceMappingURL=wheel.js.map